import {
  MyTeamDecision,
  type CreateMyTeamDelegationPayload,
  type MyTeamAccessResponse,
  type MyTeamApprovalsCountResponse,
  type MyTeamApprovalsResponse,
  type MyTeamCalendarParams,
  type MyTeamCalendarResponse,
  type MyTeamCoveringResponse,
  type MyTeamDelegateCandidatesResponse,
  type MyTeamDelegationsResponse,
  type MyTeamLeaveBalancesResponse,
  type MyTeamLeaveRequestsParams,
  type MyTeamLeaveRequestsResponse,
  type MyTeamListParams,
  type MyTeamListResponse,
  type MyTeamMemberProbationResponse,
  type MyTeamMemberResponse,
  type MyTeamProbationPayload,
  type MyTeamProbationResultResponse,
  type MyTeamSummaryResponse,
  type MyTeamTimesheetApprovalKind,
  type MyTeamTimesheetApprovalsResponse,
  type TeamOverviewResponse,
  type TeamTimeOffParams,
  type TeamTimeOffResponse,
} from '@gokrew/models/my-team';
import type { ApiResponse } from '@gokrew/models/generic';
import type {
  NeedsAttentionFeed,
  NeedsAttentionHideOptions,
  NeedsAttentionHideResult,
  NeedsAttentionPingResult,
  NeedsAttentionPrepareResult,
} from '@gokrew/models/company-dashboard';
import type { EmployeeChangeRequest } from '@gokrew/models/employee-change-requests';
import api from './client';
import type { LeaveDecisionContextResponse } from '@gokrew/models/leave-management';

/**
 * Manager My team. Like `performance.ts`, these routes resolve the company and
 * the manager from the token, so nothing is passed but the filters. The
 * server scopes every call to the caller's reporting subtree.
 */
const MY_TEAM = '/employee/my-team';
/** My team for every employee: no id is ever passed, the token is the viewer. */
const TEAM = '/employee/team';

const endpoints = {
  access: `${MY_TEAM}/access`,
  list: MY_TEAM,
  summary: `${MY_TEAM}/summary`,
  attention: `${MY_TEAM}/attention`,
  attentionPing: `${MY_TEAM}/attention/ping`,
  attentionHide: `${MY_TEAM}/attention/hide`,
  attentionUnhide: `${MY_TEAM}/attention/unhide`,
  attentionPrepare: `${MY_TEAM}/attention/prepare`,
  export: `${MY_TEAM}/export`,
  calendar: `${MY_TEAM}/leave-calendar`,
  member: (employeeId: string) => `${MY_TEAM}/members/${employeeId}`,
  probation: (employeeId: string) =>
    `${MY_TEAM}/members/${employeeId}/probation`,
  balances: (employeeId: string) =>
    `${MY_TEAM}/members/${employeeId}/leave-balances`,
  leaveRequests: (employeeId: string) =>
    `${MY_TEAM}/members/${employeeId}/leave-requests`,
  approvals: `${MY_TEAM}/approvals/leave`,
  approvalsCount: `${MY_TEAM}/approvals/leave/count`,
  approvalContext: (leaveRequestId: string) =>
    `${MY_TEAM}/approvals/leave/${leaveRequestId}/context`,
  covering: `${MY_TEAM}/approvals/covering`,
  timesheetApprovals: `${MY_TEAM}/approvals/timesheets`,
  timesheetApprovalsCount: `${MY_TEAM}/approvals/timesheets/count`,
  decideTimesheet: (
    kind: MyTeamTimesheetApprovalKind,
    id: string,
    decision: MyTeamDecision
  ) => `${MY_TEAM}/approvals/timesheets/${kind}/${id}/${decision}`,
  changeRequests: `${MY_TEAM}/approvals/change-requests`,
  changeRequestsCount: `${MY_TEAM}/approvals/change-requests/count`,
  decideChangeRequest: (id: string, decision: MyTeamDecision) =>
    `${MY_TEAM}/approvals/change-requests/${id}/${decision}`,
  decide: (leaveRequestId: string, decision: MyTeamDecision) =>
    `${MY_TEAM}/approvals/leave/${leaveRequestId}/${decision}`,
  delegations: `${MY_TEAM}/delegations`,
  delegation: (delegationId: string) =>
    `${MY_TEAM}/delegations/${delegationId}`,
  delegateCandidates: `${MY_TEAM}/delegations/candidates`,
  teamOverview: `${TEAM}/overview`,
  teamTimeOff: `${TEAM}/time-off`,
};

export async function getMyTeamAccess() {
  const { data } = await api.get<MyTeamAccessResponse>(endpoints.access);
  return data;
}

export async function getMyTeam(params: MyTeamListParams) {
  const { data } = await api.get<MyTeamListResponse>(endpoints.list, {
    params,
  });
  return data;
}

export async function getMyTeamSummary() {
  const { data } = await api.get<MyTeamSummaryResponse>(endpoints.summary);
  return data;
}

/** The manager's "Needs attention" feed, subtree only. */
export async function getMyTeamAttention() {
  const { data } = await api.get<ApiResponse<NeedsAttentionFeed>>(
    endpoints.attention
  );
  return data;
}

/** "Notify" on a line of the manager's feed; the server picks who is told. */
export async function pingMyTeamAttention(itemId: string) {
  const { data } = await api.post<ApiResponse<NeedsAttentionPingResult>>(
    endpoints.attentionPing,
    { itemId }
  );
  return data;
}

/**
 * Hide a line of the manager's feed (or a rollup row's lines) for this
 * manager only. Answers the line ids hidden, which Undo sends back.
 */
export async function hideMyTeamAttention(
  itemId: string,
  options: NeedsAttentionHideOptions = {}
) {
  const { until, reason } = options;
  const { data } = await api.post<ApiResponse<NeedsAttentionHideResult>>(
    endpoints.attentionHide,
    { itemId, ...(until ? { until } : {}), ...(reason ? { reason } : {}) }
  );
  return data;
}

/**
 * "Prepare the meeting" on a vacation or overtime line of the manager's
 * feed. Only the line id is sent; no name ever reaches the model.
 */
export async function prepareMyTeamAttention(itemId: string) {
  const { data } = await api.post<ApiResponse<NeedsAttentionPrepareResult>>(
    endpoints.attentionPrepare,
    { itemId }
  );
  return data.data;
}

/** Show lines this manager hid again. */
export async function unhideMyTeamAttention(itemIds: string[]) {
  const { data } = await api.post<ApiResponse<NeedsAttentionHideResult>>(
    endpoints.attentionUnhide,
    { itemIds }
  );
  return data;
}

/** The list view as CSV, same filters as the list. */
export async function exportMyTeam(params: Omit<MyTeamListParams, 'page'>) {
  const { data } = await api.get<Blob>(endpoints.export, {
    params,
    responseType: 'blob',
  });
  return data;
}

export async function getMyTeamCalendar(params: MyTeamCalendarParams) {
  const { data } = await api.get<MyTeamCalendarResponse>(endpoints.calendar, {
    params,
  });
  return data;
}

/**
 * The member page renders its own "not in your team" state for a 403/404,
 * so the global error toast stays quiet for those.
 */
export async function getMyTeamMember(employeeId: string) {
  const { data } = await api.get<MyTeamMemberResponse>(
    endpoints.member(employeeId),
    { quietStatuses: [403, 404] }
  );
  return data;
}

/** A team member's probation: end date, my options, open recommendations. */
export async function getMyTeamMemberProbation(employeeId: string) {
  const { data } = await api.get<MyTeamMemberProbationResponse>(
    endpoints.probation(employeeId)
  );
  return data;
}

/**
 * Confirm, extend or ask HR to discuss a probation (2026-10-11). The server
 * applies it directly for a manager allowed to decide, otherwise it records
 * a recommendation for HR.
 */
export async function actOnMyTeamProbation(
  employeeId: string,
  payload: MyTeamProbationPayload
) {
  const { action, ...body } = payload;
  const { data } = await api.post<MyTeamProbationResultResponse>(
    `${endpoints.probation(employeeId)}/${action}`,
    body
  );
  return data;
}

export async function getMyTeamMemberBalances(employeeId: string) {
  const { data } = await api.get<MyTeamLeaveBalancesResponse>(
    endpoints.balances(employeeId),
    { quietStatuses: [403, 404] }
  );
  return data;
}

export async function getMyTeamMemberLeaveRequests(
  employeeId: string,
  params: MyTeamLeaveRequestsParams
) {
  const { data } = await api.get<MyTeamLeaveRequestsResponse>(
    endpoints.leaveRequests(employeeId),
    { params, quietStatuses: [403, 404] }
  );
  return data;
}

/** Pending leave requests I may decide (as manager or delegate). */
export async function getMyTeamApprovals() {
  const { data } = await api.get<MyTeamApprovalsResponse>(endpoints.approvals);
  return data;
}

/** Manager, wait, balance and team overlap of one request I may decide. */
export async function getMyTeamApprovalContext(leaveRequestId: string) {
  const { data } = await api.get<LeaveDecisionContextResponse>(
    endpoints.approvalContext(leaveRequestId)
  );
  return data;
}

export async function getMyTeamApprovalsCount() {
  const { data } = await api.get<MyTeamApprovalsCountResponse>(
    endpoints.approvalsCount
  );
  return data;
}

/** Profile changes of my direct reports routed to me (change rules). */
export async function getMyTeamChangeRequests() {
  const { data } = await api.get<ApiResponse<EmployeeChangeRequest[]>>(
    endpoints.changeRequests
  );
  return data;
}

export async function getMyTeamChangeRequestsCount() {
  const { data } = await api.get<MyTeamApprovalsCountResponse>(
    endpoints.changeRequestsCount
  );
  return data;
}

/**
 * Approve, or reject with a reason. A request decided meanwhile (409) or no
 * longer mine (404) is explained in place, never as a global toast.
 */
export async function decideMyTeamChangeRequest(
  id: string,
  decision: MyTeamDecision,
  reason?: string
) {
  const body =
    decision === MyTeamDecision.Approve ? {} : { reason: reason ?? '' };
  const { data } = await api.put(
    endpoints.decideChangeRequest(id, decision),
    body,
    {
      quietStatuses: [404, 409],
    }
  );
  return data;
}

/** Timesheet weeks and changes I may decide (manager, escalation, delegate). */
export async function getMyTeamTimesheetApprovals() {
  const { data } = await api.get<MyTeamTimesheetApprovalsResponse>(
    endpoints.timesheetApprovals
  );
  return data;
}

export async function getMyTeamTimesheetApprovalsCount() {
  const { data } = await api.get<MyTeamApprovalsCountResponse>(
    endpoints.timesheetApprovalsCount
  );
  return data;
}

/**
 * Approve or send back a week or a change. A row that moved on or was
 * decided meanwhile (403/404/409) is explained in place, never toasted.
 */
export async function decideMyTeamTimesheet(
  kind: MyTeamTimesheetApprovalKind,
  id: string,
  decision: MyTeamDecision,
  note?: string
) {
  const { data } = await api.put<
    ApiResponse<{ id: string; status: string; stepAdvanced: boolean }>
  >(
    endpoints.decideTimesheet(kind, id, decision),
    { note: note || undefined },
    { quietStatuses: [403, 404, 409] }
  );
  return data;
}

export async function getMyTeamCovering() {
  const { data } = await api.get<MyTeamCoveringResponse>(endpoints.covering);
  return data;
}

/**
 * Approve or reject. The Approvals tab explains a 403/409 in place ("moved to
 * X", "already decided"), so those never toast or redirect globally.
 */
export async function decideMyTeamLeave(
  leaveRequestId: string,
  decision: MyTeamDecision,
  comment?: string
) {
  const body =
    decision === MyTeamDecision.Approve
      ? { comment: comment || undefined }
      : { rejectionReason: comment || undefined };
  const { data } = await api.put(
    endpoints.decide(leaveRequestId, decision),
    body,
    {
      quietStatuses: [403, 404, 409],
    }
  );
  return data;
}

export async function getMyTeamDelegations() {
  const { data } = await api.get<MyTeamDelegationsResponse>(
    endpoints.delegations
  );
  return data;
}

export async function getMyTeamDelegateCandidates(search: string) {
  const { data } = await api.get<MyTeamDelegateCandidatesResponse>(
    endpoints.delegateCandidates,
    { params: { search: search || undefined } }
  );
  return data;
}

export async function createMyTeamDelegation(
  payload: CreateMyTeamDelegationPayload
) {
  const { data } = await api.post(endpoints.delegations, payload);
  return data;
}

export async function revokeMyTeamDelegation(delegationId: string) {
  const { data } = await api.delete(endpoints.delegation(delegationId));
  return data;
}

/** My manager, my teammates and my direct reports (every employee). */
export async function getTeamOverview() {
  const { data } = await api.get<TeamOverviewResponse>(endpoints.teamOverview);
  return data;
}

/** Approved leave of my team, or of the company when I have no team yet. */
export async function getTeamTimeOff(params: TeamTimeOffParams = {}) {
  const { data } = await api.get<TeamTimeOffResponse>(endpoints.teamTimeOff, {
    params,
  });
  return data;
}
